/**
 * Guests: who they are, what they cannot eat, what they have had, and whether
 * they agreed to hear from the restaurant.
 *
 * The till finds a guest and puts them on a tab; the back office keeps the
 * list tidy. Visits and spend are worked out by the server from the tabs
 * themselves — nothing here adds money up.
 *
 * Consent is a history, and what a guest wants now is the latest entry per
 * channel. `given: null` is "nobody asked", which is not a yes.
 */

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiError } from './client';
import { useApi } from './provider';

export type ConsentChannel = 'email' | 'sms';
/** Who wrote a consent row. `merge` and `erasure` are written by the system. */
export type ConsentSource = 'till' | 'back_office' | 'import' | 'merge' | 'erasure';

export const CONSENT_CHANNEL_LABELS: Record<ConsentChannel, string> = {
  email: 'Emails',
  sms: 'Texts',
};

export const CONSENT_SOURCE_LABELS: Record<ConsentSource, string> = {
  till: 'at the till',
  back_office: 'in the back office',
  import: 'from an imported list',
  merge: 'when two profiles were merged',
  erasure: 'when the guest was erased',
};

export interface ConsentState {
  channel: ConsentChannel;
  /** Null: never asked, which is not a yes. */
  given: boolean | null;
  at: string | null;
  source: ConsentSource | null;
}

export interface Guest {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
  notes: string | null;
  /** Codes from the fourteen allergens — see `ALLERGEN_LABELS`. */
  allergens: string[];
  dietary: string | null;
  tags: string[];
  created_at: string;
  updated_at: string;
  consent: ConsentState[];
}

export interface GuestInput {
  name: string;
  phone?: string | null;
  email?: string | null;
  notes?: string | null;
  allergens?: string[];
  dietary?: string | null;
  tags?: string[];
}

export interface GuestHistory {
  /** Settled tabs. One still open is tonight, not history. */
  visits: number;
  /** What those came to, tax in, tips out. */
  spend: string;
  first_visit: string | null;
  last_visit: string | null;
  favourites: { item_id: string; name: string; quantity: string }[];
  recent: {
    order_id: string;
    location_id: string;
    number: number;
    business_date: string;
    total: string;
  }[];
}

export interface ConsentRecord {
  id: string;
  channel: ConsentChannel;
  given: boolean;
  source: ConsentSource;
  recorded_by: string | null;
  recorded_by_name: string | null;
  recorded_at: string;
}

/** What a guest wants on one channel now. */
export function consentOn(guest: Guest, channel: ConsentChannel): ConsentState {
  return (
    guest.consent.find((state) => state.channel === channel) ?? {
      channel,
      given: null,
      at: null,
      source: null,
    }
  );
}

/** "Yes", "No", or "Not asked" — the last never read as either of the others. */
export function describeConsent(state: ConsentState): string {
  if (state.given === null) return 'Not asked';
  return state.given ? 'Yes' : 'No';
}

/** Guests by name, email or phone. Everybody, alphabetically, with no query. */
export function useGuests(query: string, options?: { enabled?: boolean; limit?: number }) {
  const api = useApi();
  const q = query.trim();
  const limit = options?.limit ?? 30;
  return useQuery({
    queryKey: ['customers', 'list', q, limit],
    queryFn: ({ signal }) =>
      api.get<Guest[]>(
        `/v1/customers?limit=${limit}${q ? `&q=${encodeURIComponent(q)}` : ''}`,
        signal,
      ),
    enabled: options?.enabled ?? true,
    placeholderData: keepPreviousData,
    staleTime: 10_000,
  });
}

export function useGuest(
  customerId: string | null | undefined,
  options?: { enabled?: boolean },
) {
  const api = useApi();
  return useQuery({
    queryKey: ['customers', 'detail', customerId ?? null],
    queryFn: ({ signal }) => api.get<Guest>(`/v1/customers/${customerId}`, signal),
    enabled: Boolean(customerId) && (options?.enabled ?? true),
    staleTime: 10_000,
  });
}

export function useGuestHistory(customerId: string | null | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ['customers', 'history', customerId ?? null],
    queryFn: ({ signal }) =>
      api.get<GuestHistory>(`/v1/customers/${customerId}/history`, signal),
    enabled: Boolean(customerId),
  });
}

/** Others with the same email, phone or name. Offered for merging, never merged. */
export function useGuestDuplicates(customerId: string | null | undefined, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: ['customers', 'duplicates', customerId ?? null],
    queryFn: ({ signal }) => api.get<Guest[]>(`/v1/customers/${customerId}/duplicates`, signal),
    enabled: Boolean(customerId) && enabled,
  });
}

export function useConsentHistory(customerId: string | null | undefined, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: ['customers', 'consent', customerId ?? null],
    queryFn: ({ signal }) =>
      api.get<ConsentRecord[]>(`/v1/customers/${customerId}/consent`, signal),
    enabled: Boolean(customerId) && enabled,
  });
}

function useGuestMutation<TInput, TResult>(
  send: (api: ReturnType<typeof useApi>, input: TInput) => Promise<TResult>,
  also: (string | null)[][] = [],
) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<TResult, ApiError, TInput>({
    mutationFn: (input) => send(api, input),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['customers'] });
      for (const key of also) void queryClient.invalidateQueries({ queryKey: key });
    },
  });
}

/** 409 on an email, phone or allergen the server cannot take, with `field`. */
export function useAddGuest() {
  return useGuestMutation<GuestInput, Guest>((api, input) =>
    api.post<Guest>('/v1/customers', input),
  );
}

/** Only the fields sent change. A field sent as null is cleared. */
export function useUpdateGuest() {
  return useGuestMutation<{ id: string } & Partial<GuestInput>, Guest>((api, { id, ...body }) =>
    api.request<Guest>(`/v1/customers/${id}`, { method: 'PATCH', body }),
  );
}

/** A "yes" or a "no", added to the history. A "yes" needs an address to send to. */
export function useRecordConsent() {
  return useGuestMutation<
    { id: string; channel: ConsentChannel; given: boolean; source: 'till' | 'back_office' },
    Guest
  >((api, { id, ...body }) => api.post<Guest>(`/v1/customers/${id}/consent`, body));
}

/** Fold `mergeId` into `keepId`. The duplicate is gone afterwards. */
export function useMergeGuests() {
  return useGuestMutation<{ keepId: string; mergeId: string }, Guest>(
    (api, { keepId, mergeId }) =>
      api.post<Guest>(`/v1/customers/${keepId}/merge`, { merge_id: mergeId }),
    [['orders']],
  );
}

/** Remove a guest's personal details for good. Their tabs stay, no longer theirs. */
export function useEraseGuest() {
  return useGuestMutation<{ id: string }, { customer_id: string; tabs: number }>(
    (api, { id }) => api.post(`/v1/customers/${id}/erase`),
    [['orders']],
  );
}

function useTabGuestMutation<TInput extends { orderId: string }, TResult>(
  send: (api: ReturnType<typeof useApi>, input: TInput) => Promise<TResult>,
) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<TResult, ApiError, TInput>({
    mutationFn: (input) => send(api, input),
    onSettled: (_data, _error, input) => {
      void queryClient.invalidateQueries({ queryKey: ['orders', 'detail', input.orderId] });
      void queryClient.invalidateQueries({ queryKey: ['orders', 'ledger', input.orderId] });
      void queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
  });
}

/** Say who a tab is for. Replaces whoever it was for. */
export function useAttachGuest() {
  return useTabGuestMutation<{ orderId: string; customerId: string }, Guest>((api, input) =>
    api.request<Guest>(`/v1/orders/${input.orderId}/guest`, {
      method: 'PUT',
      body: { customer_id: input.customerId },
    }),
  );
}

export function useDetachGuest() {
  return useTabGuestMutation<{ orderId: string }, void>((api, input) =>
    api.request<void>(`/v1/orders/${input.orderId}/guest`, { method: 'DELETE' }),
  );
}

/**
 * The path of the campaign file: everybody whose latest word on the channel is
 * yes. For `useDownload`, which carries the token. On the audit log each time.
 */
export function consentedContactsUrl(channel: ConsentChannel): string {
  return `/v1/customers/consented.csv?channel=${channel}`;
}
